"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HTTPClient = void 0;
const http_helpers_1 = require("./http-helpers");
const ApiError_1 = require("./ApiError");
const httpStatusEnum_1 = require("./enums/httpStatusEnum");
const runtime_config_1 = require("../config/runtime-config");
const buildApiUrl_1 = require("./utils/buildApiUrl");
const AuthContext_1 = require("../../hooks/auth/context/AuthContext");
const getRequestHeaders = async (headers) => {
    try {
        const token = await (0, AuthContext_1.getToken)();
        return {
            ...(token && { Authorization: `Bearer ${token}` }),
            ...headers,
        };
    }
    catch {
        return { ...headers };
    }
};
const createRequest = async (rp, method) => {
    let { url, params } = rp;
    const { type, body, options } = rp;
    const headers = await getRequestHeaders(rp.headers);
    try {
        const config = (0, runtime_config_1.getConfig)();
        if (params && !url.startsWith('http')) {
            url = (0, buildApiUrl_1.buildApiUrl)(url, params);
            params = undefined;
        }
        if (params) {
            const baseUrl = url.startsWith('http') ? url : config.API_URL;
            const urlObj = new URL(url, baseUrl);
            Object.entries(params).forEach(([key, value]) => {
                if (value != null)
                    urlObj.searchParams.set(key, String(value));
            });
            url = urlObj.toString();
        }
        if (!url.startsWith('http')) {
            const base = config.API_URL.endsWith('/')
                ? config.API_URL.slice(0, -1)
                : config.API_URL;
            url = `${base}${url.startsWith('/') ? '' : '/'}${url}`;
        }
    }
    catch (error) {
        console.error('Error building request URL:', error);
        throw new Error('Error de configuración al construir la URL', {
            cause: error,
        });
    }
    const requestHeaders = createHeaders(headers, type);
    const requestBody = createBody(body, type);
    const requestOptions = createOptions(options);
    return new Request(url, {
        method,
        headers: requestHeaders,
        body: requestBody,
        ...requestOptions,
    });
};
const createHeaders = (headers, type) => {
    const h = new Headers();
    if (headers) {
        for (const [key, value] of Object.entries(headers)) {
            h.append(key, value);
        }
    }
    if (type === 'json') {
        h.set('Content-Type', 'application/json');
    }
    return h;
};
const createBody = (body, type) => {
    if (!body)
        return undefined;
    switch (type) {
        case 'json':
            return JSON.stringify(body);
        case 'form':
        case 'text':
            return body;
        case 'binary':
        default:
            return '';
    }
};
const createOptions = (options) => {
    const defaultOptions = {
        credentials: 'include',
        redirect: 'manual',
    };
    return options != null ? { ...defaultOptions, ...options } : defaultOptions;
};
function isError(error) {
    return error instanceof Error;
}
function normalizeError(error) {
    if (error instanceof TypeError) {
        return { type: 'NETWORK_ERROR', message: error.message };
    }
    // Se conserva la instancia original (p.ej. ApiError) para no perder
    // el status/title/detail que envía el backend.
    if (isError(error))
        return error;
    return new ApiError_1.ApiError(String(error));
}
exports.HTTPClient = {
    get: async (p) => {
        try {
            const req = await createRequest(p, 'GET'); // 👈 ahora espera a que se cree con token fresco
            const res = await fetch(req);
            return handleResponse(res);
        }
        catch (error) {
            const normalizedError = normalizeError(error);
            console.error('GET request failed:', normalizedError);
            throw normalizedError;
        }
    },
    post: async (p) => {
        try {
            const req = await createRequest(p, 'POST');
            const res = await fetch(req);
            return handleResponse(res);
        }
        catch (error) {
            const normalizedError = normalizeError(error);
            console.error('POST request failed:', normalizedError);
            throw normalizedError;
        }
    },
    put: async (p) => {
        try {
            const req = await createRequest(p, 'PUT');
            const res = await fetch(req);
            return handleResponse(res);
        }
        catch (error) {
            const normalizedError = normalizeError(error);
            console.error('PUT request failed:', normalizedError);
            throw normalizedError;
        }
    },
    patch: async (p) => {
        try {
            const req = await createRequest(p, 'PATCH');
            const res = await fetch(req);
            return handleResponse(res);
        }
        catch (error) {
            const normalizedError = normalizeError(error);
            console.error('PATCH request failed:', normalizedError);
            throw normalizedError;
        }
    },
    delete: async (p) => {
        try {
            const req = await createRequest(p, 'DELETE'); // 👈 ahora espera al request con token actualizado
            const res = await fetch(req);
            if (res.status === 0) {
                throw { type: 'NETWORK_ERROR', message: 'Error de red' };
            }
            if (![httpStatusEnum_1.HTTPStatus.OK, httpStatusEnum_1.HTTPStatus.NoContent, httpStatusEnum_1.HTTPStatus.Accepted].includes(res.status)) {
                const error = await (0, http_helpers_1.getResponseError)(res);
                throw error;
            }
            return true;
        }
        catch (error) {
            const normalizedError = normalizeError(error);
            console.error('DELETE request failed:', normalizedError);
            if ('type' in normalizedError &&
                normalizedError.type === 'NETWORK_ERROR') {
                window.location.reload();
            }
            throw normalizedError;
        }
    },
};
const handleResponse = async (res) => {
    if (res.status === 0) {
        console.warn('Posible sesión expirada o problema de red. Recargando...');
        window.location.reload();
        return Promise.reject(new Error('Error de red'));
    }
    if (res.status === httpStatusEnum_1.HTTPStatus.NoContent) {
        return {};
    }
    if (!res.ok) {
        const error = await (0, http_helpers_1.getResponseError)(res);
        throw error;
    }
    const contentType = res.headers.get('content-type');
    const hasContent = res.headers.get('content-length') !== '0';
    if (!hasContent) {
        return {};
    }
    if (contentType?.includes('application/json')) {
        return (await res.json());
    }
    if (contentType?.includes('text/plain')) {
        return (await res.text());
    }
    if (contentType?.includes('application/octet-stream') ||
        contentType?.includes('application/pdf') ||
        contentType?.startsWith('image/')) {
        return (await res.blob());
    }
    // fallback: devolver texto si no sabemos qué es
    return (await res.text());
};
